import { FormEvent, useMemo, useState } from 'react';
import { Link, useLocation, useSearch } from 'wouter';

import {
  AuthApiError,
  completePasswordReset,
  confirmEmailVerification,
  login,
  requestEmailVerification,
  requestPasswordReset,
} from '../../api/auth';
import { PasswordField, passwordAcceptable, passwordProblem } from '../../components/PasswordField';
import { useSession } from '../../session';
import { getPendingOffer } from '../../lib/pendingOffer';

/** Where a brand-new account goes: Welcome, carrying a plan chosen before sign-up on to checkout. */
function welcomeDestination(): string {
  return getPendingOffer() ? '/welcome?return_to=' + encodeURIComponent('/billing/checkout') : '/welcome';
}

function messageFor(error: unknown): string {
  return error instanceof AuthApiError ? error.message : 'the request could not be completed';
}

/**
 * One page for both emailed links that set a password (F-1/F-4): an invited person choosing their
 * first password, and an existing customer resetting theirs. Both arrive on the same "Reset password"
 * email template, so the page cannot tell them apart before the token is used; the copy works for
 * both, and the server's `account_created` answer decides what follows. After the password is set the
 * page signs in with it (exactly what the person could do by hand) and continues: a new account goes
 * to Welcome, where the terms come first.
 */
export function PasswordResetPage() {
  const search = useSearch();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const token = useMemo(() => params.get('token') || '', [params]);
  const { setSession } = useSession();
  const [, navigate] = useLocation();
  const [email, setEmail] = useState(params.get('email') || '');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState<'' | 'created' | 'reset'>('');

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      if (token) {
        if (!passwordAcceptable(password)) {
          setError(passwordProblem(password));
          return;
        }
        const result = await completePasswordReset({ token, password, email: email || undefined });
        const chosen = password;
        setPassword('');
        setDone(result.accountCreated ? 'created' : 'reset');
        if (email) {
          try {
            const session = await login({ email, password: chosen });
            setSession(session);
            navigate(result.accountCreated ? welcomeDestination() : '/projects', { replace: true });
            return;
          } catch {
            // Fall through: the password is set; the person signs in on the next screen.
          }
        }
      } else {
        setNotice(await requestPasswordReset(email));
      }
    } catch (failure) {
      setError(messageFor(failure));
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    const signIn = '/login' + (email ? '?email=' + encodeURIComponent(email) : '');
    return (
      <main className="flow-card">
        <p className="eyebrow">{done === 'created' ? 'Welcome to Auteur' : 'Account security'}</p>
        <h1>{done === 'created' ? 'Your account is ready' : 'Your password is updated'}</h1>
        <p role="status" className="success">
          {done === 'created'
            ? 'Your password is set. Sign in to continue to your setup.'
            : 'Your new password is set, and any other signed-in browsers were signed out. Sign in to continue.'}
        </p>
        <Link to={signIn}>Sign in</Link>
      </main>
    );
  }

  return (
    <main className="flow-card">
      <p className="eyebrow">{token ? 'Your Auteur account' : 'Account recovery'}</p>
      <h1>{token ? 'Choose your password' : 'Reset your password'}</h1>
      {token ? (
        <p>
          New to Auteur? Choose a password to finish creating your account and we’ll sign you in.
          Here because you forgot your password? Choose a new one below.
        </p>
      ) : null}
      <form onSubmit={submit}>
        <label htmlFor="reset-email">Email</label>
        <input id="reset-email" type="email" autoComplete="username" value={email} onChange={(event) => setEmail(event.target.value)} required />
        {token ? (
          <PasswordField id="reset-password" label="Password" autoComplete="new-password" showRules
            value={password} disabled={busy} onChange={(value) => { setPassword(value); setError(''); }} />
        ) : null}
        {error ? <p role="alert" className="error">{error}</p> : null}
        {notice ? <p role="status" className="success">{notice}</p> : null}
        <button disabled={busy || !email || (Boolean(token) && !passwordAcceptable(password))}>
          {busy ? 'Working…' : token ? 'Set password and continue' : 'Send reset link'}
        </button>
      </form>
      <Link to="/login">Back to sign in</Link>
    </main>
  );
}

export function EmailVerificationPage() {
  const search = useSearch();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const token = useMemo(() => params.get('token') || '', [params]);
  const [email, setEmail] = useState(params.get('email') || '');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    setNotice('');
    try {
      if (token) {
        const verified = await confirmEmailVerification(token, email || undefined);
        setNotice(verified ? 'Email address verified. Your account is ready: sign in to continue to your setup.' : 'Verification was not confirmed.');
      } else {
        setNotice(await requestEmailVerification(email));
      }
    } catch (failure) {
      setError(messageFor(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="flow-card">
      <p className="eyebrow">Account security</p>
      <h1>{token ? 'Verify your email' : 'Request a verification email'}</h1>
      <form onSubmit={submit}>
        <label htmlFor="verification-email">Email</label>
        <input id="verification-email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} required={!token} />
        {error ? <p role="alert" className="error">{error}</p> : null}
        {notice ? <p role="status" className="success">{notice}</p> : null}
        <button disabled={busy || (!token && !email)}>{busy ? 'Working…' : token ? 'Verify email' : 'Send verification link'}</button>
      </form>
      <Link to="/login">Back to sign in</Link>
    </main>
  );
}
